'use client';

import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { supabase } from '../supabaseClient';

function RemoveMemberModal({
  isOpen,
  onClose,
  member,
  teamMembers,
  onRemoveSuccess,
  organizationId,
}) {
  const [measurementCount, setMeasurementCount] = useState(0);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false); // Start closed, set based on measurements
  const [transferToUser, setTransferToUser] = useState(null);
  const [loading, setLoading] = useState(false);

  // Use effect unconditionally, handling null/undefined member
  useEffect(() => {
    if (!isOpen || !member || !member.userid) return; // Updated to use userid

    setTransferToUser(''); // Reset transfer user

    const fetchMeasurements = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('measurements')
          .select('id')
          .eq('profile_id', member.userid); // Updated to use userid

        if (error) {
          console.error('Error fetching measurements:', error);
          alert('Failed to check measurements.');
          return;
        }

        setMeasurementCount(data.length);
        setIsTransferModalOpen(data.length > 0); // Show transfer modal if measurements exist
      } catch (err) {
        console.error('Unexpected error checking measurements:', err);
        alert('Failed to check measurements.');
      } finally {
        setLoading(false);
      }
    };

    fetchMeasurements();
  }, [isOpen, member]); // Depend on isOpen and member to re-run when they change

  // Handle measurement transfer and member removal
  const handleTransferAndRemove = async () => {
    if (!member || !transferToUser) {
      alert('Please select a team member to transfer measurements to.');
      return;
    }

    setLoading(true);
    try {
      // Transfer measurements to the selected user
      const { error: transferError } = await supabase
        .from('measurements')
        .update({ profile_id: transferToUser.userid }) // Updated to use userid
        .eq('profile_id', member.userid); // Updated to use userid

      if (transferError) {
        console.error('Error transferring measurements:', transferError);
        alert('Failed to transfer measurements.');
        return;
      }

      // Remove the member from the organization
      const { error: removeError } = await supabase
        .from('organization_members')
        .delete()
        .eq('user_id', member.userid); // Updated to use userid

      if (removeError) {
        console.error('Error removing member:', removeError);
        alert('Failed to remove member.');
      } else {
        alert('Member removed successfully, measurements transferred.');
        onRemoveSuccess(); // Refresh team list
      }
    } catch (err) {
      console.error('Unexpected error removing member:', err);
      alert('Failed to remove member.');
    } finally {
      setLoading(false);
      onClose(); // Close the modal
    }
  };

  // Handle direct removal (no measurements)
  const handleConfirmRemove = async () => {
    if (!member) {
      alert('No member selected for removal.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('organization_members')
        .delete()
        .eq('user_id', member.userid); // Updated to use userid

      if (error) {
        console.error('Error removing member:', error);
        alert('Failed to remove member.');
      } else {
        alert('Member removed successfully.');
        onRemoveSuccess(); // Refresh team list
      }
    } catch (err) {
      console.error('Unexpected error removing member:', err);
      alert('Failed to remove member.');
    } finally {
      setLoading(false);
      onClose(); // Close the modal
    }
  };

  // Render nothing if member or isOpen is invalid, but allow hooks to run
  if (!isOpen || !member || !member.userid) { // Updated to use userid
    return null;
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                {isTransferModalOpen ? (
                  <>
                    <div>
                      <div>
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-medium leading-6 text-gray-900"
                        >
                          Transfer Measurements Before Removal
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-black font-bold">
                            {member.firstName} {member.lastName} has {measurementCount}{' '}
                            measurements.
                          </p>
                          <p className="text-sm text-gray-500">
                            Select a team member to transfer these measurements to
                            before removing them from the organization.
                          </p>
                          <select
                            value={transferToUser?.userid || ''} // Updated to use userid
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              setTransferToUser(
                                teamMembers.find((m) => m.userid === selectedId && !m.isInvited) // Updated to use userid
                              );
                            }}
                            className="mt-4 w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500"
                            disabled={loading}
                          >
                            <option value="">Select a team member</option>
                            {teamMembers
                              .filter((m) => m.userid !== member.userid && !m.isInvited) // Updated to use userid
                              .map((teamMember) => (
                                <option key={teamMember.userid} value={teamMember.userid}> 
                                  {teamMember.firstName} {teamMember.lastName} ({teamMember.role})
                                </option>
                              ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-6">
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                          onClick={() => { onClose(); setTransferToUser(''); }}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                          onClick={handleTransferAndRemove}
                          disabled={loading || !transferToUser}
                        >
                          {loading ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="text-center sm:mt-5">
                        <Dialog.Title
                          as="h3"
                          className="text-lg font-medium leading-6 text-gray-900"
                        >
                          Confirm Removal
                        </Dialog.Title>
                        <div className="mt-2">
                          <p className="text-sm text-gray-500">
                            Are you sure you want to remove {member.firstName}{' '}
                            {member.lastName} from the organization? This action cannot be undone.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-5 sm:mt-6">
                      <div className="flex justify-end space-x-2">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                          onClick={onClose}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-yellow-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
                          onClick={handleConfirmRemove}
                          disabled={loading}
                        >
                          {loading ? 'Removing...' : 'Remove'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

export default RemoveMemberModal;